import { useLingui } from "@lingui/react/macro";
import { ParkingOption } from "@offstreetparking/design-system/dataDisplay/ParkingOption";
import { ParkingIcon } from "@offstreetparking/design-system/icons/ParkingIcon";
import { Text } from "@offstreetparking/design-system/typography";
import {
	type StepId,
	stepIds,
	usePermitActiveStep,
	usePermitFormActions,
	usePermitFormData,
	usePermitSteps,
} from "../../PermitContextProvider";
import { usePermitWizardSettings } from "../../PermitContextProvider/PermitContextProvider";
import { splitChoicesByAvailability } from "../../splitChoicesByAvailability";
import { LocationButtonList } from "../LocationButtonList";
import { SummaryContent } from "./SummaryContent";
type ParkingSummaryProps = {
	timezone: string;
	hideLocation?: boolean;
};
export const ParkingSummary = ({ timezone, hideLocation }: ParkingSummaryProps) => {
	const { t } = useLingui();
	const permit = usePermitFormData();
	const wizardSettings = usePermitWizardSettings();
	const { goToStep } = usePermitFormActions();
	const activeStep = usePermitActiveStep();
	const steps = usePermitSteps();
	if (!permit.prev && steps.includes(stepIds.parkingTime)) {
		return <div className="border-b-2 border-b-neutral-faded" />;
	}
	const activeStepIndex = steps.indexOf(activeStep);
	const isStepInPast = (stepId: StepId) => steps.indexOf(stepId) < activeStepIndex;
	const selectedParkingOption = wizardSettings.choices.find((x) => x.permitTypeId === permit.parkingChoiceId);
	const { availableChoices } = splitChoicesByAvailability(wizardSettings.choices);
	const locationIds = permit.locationIds ?? [];
	const permitLocations = selectedParkingOption
		? locationIds.length > 0
			? selectedParkingOption.locations.filter((l) => locationIds.includes(l.id))
			: selectedParkingOption.locations
		: [];
	return (
		<div className="border-b-2 border-b-neutral-faded">
			{steps.includes(stepIds.parkingTime) &&
				isStepInPast(stepIds.parkingTime) &&
				selectedParkingOption &&
				availableChoices.length > 1 && (
					<div
						data-summary-item
						data-summary-step={stepIds.parkingTime}
						className="border-b border-b-neutral-faded px-4 py-3 md:px-8 md:py-4"
					>
						<SummaryContent
							title={
								<span className="inline-flex items-center gap-2">
									<ParkingIcon size={16} />
									{t`Parking Options`}
								</span>
							}
							action={{
								label: t`Change`,
								ariaLabel: t`Change Parking Options`,
								onClick: () => goToStep(stepIds.parkingTime),
							}}
						>
							<Text size="sm">{selectedParkingOption.title}</Text>
						</SummaryContent>
					</div>
				)}

			{(!steps.includes(stepIds.parkingTime) ||
				(isStepInPast(stepIds.parkingTime) && availableChoices.length === 1)) &&
				selectedParkingOption && (
					<div
						data-summary-item
						data-summary-step={stepIds.parkingTime}
						className="border-b border-b-neutral-faded px-4 py-3 md:px-8 md:py-4"
					>
						<SummaryContent
							title={t`Parking Details`}
							action={
								steps.includes(stepIds.parkingTime)
									? {
											label: t`Change`,
											ariaLabel: t`Change Parking Details`,
											onClick: () => goToStep(stepIds.parkingTime),
										}
									: undefined
							}
						>
							<ParkingOption
								timezone={timezone}
								permitIntervals={permit.permitIntervals}
								isInstant={
									!!selectedParkingOption.time?.startDate?.instant &&
									!!selectedParkingOption.time?.startTime?.instant
								}
							/>
							{!hideLocation && (
								<div className="mt-2">
									<LocationButtonList locations={permitLocations} variant="secondary" collapsible />
								</div>
							)}
						</SummaryContent>
					</div>
				)}

			{isStepInPast(stepIds.parkingDetails) &&
				((permit.vehicles && permit.vehicles.length > 0) || permit.email || permit.phoneNumber) && (
					<div
						data-summary-item
						data-summary-step={stepIds.parkingDetails}
						className="border-b border-b-neutral-faded px-4 py-3 md:px-8 md:py-4"
					>
						<SummaryContent
							title={t`Permit Information`}
							action={{
								label: t`Change`,
								ariaLabel: t`Change Permit Information`,
								onClick: () => goToStep(stepIds.parkingDetails),
							}}
						>
							<div className="space-y-1">
								{permit.vehicles?.map((vehicle) => (
									<div key={`${vehicle.plate}-${vehicle.state}`} className="flex justify-between">
										<Text size="sm">{t`License Plate`}</Text>
										<Text size="sm">
											{vehicle.plate} - {vehicle.state}
										</Text>
									</div>
								))}
								{permit.email && (
									<div className="flex justify-between">
										<Text size="sm">{t`Email`}</Text>
										<Text size="sm">{permit.email}</Text>
									</div>
								)}
								{permit.phoneNumber && (
									<div className="flex justify-between">
										<Text size="sm">{t`Phone Number`}</Text>
										<Text size="sm">{permit.phoneNumber}</Text>
									</div>
								)}
							</div>
						</SummaryContent>
					</div>
				)}

			{isStepInPast(stepIds.additionalInfo) &&
				wizardSettings.additionalInformation.enabled &&
				permit.fields &&
				permit.fields.length > 0 && (
					<div
						data-summary-item
						data-summary-step={stepIds.additionalInfo}
						className="border-b border-b-neutral-faded px-4 py-3 md:px-8 md:py-4"
					>
						<SummaryContent
							title={t`Additional Information`}
							action={{
								label: t`Change`,
								ariaLabel: t`Change Additional Information`,
								onClick: () => goToStep(stepIds.additionalInfo),
							}}
						>
							<div className="space-y-1">
								{permit.fields.map((field) => (
									<div key={field.permitFieldId} className="flex justify-between">
										<Text size="sm">{field.label}</Text>
										<Text size="sm">{field.value}</Text>
									</div>
								))}
							</div>
						</SummaryContent>
					</div>
				)}

			{isStepInPast(stepIds.terms) && wizardSettings.isTermsEnabled && (
				<div
					data-summary-item
					data-summary-step={stepIds.terms}
					className="border-b border-b-neutral-faded px-4 py-3 md:px-8 md:py-4"
				>
					<SummaryContent title={t`Parker Terms`}>
						<Text size="sm">{t`All terms accepted.`}</Text>
					</SummaryContent>
				</div>
			)}
		</div>
	);
};
